/**
 * Кейс контура (слайды 14–15 деки) и общая форма ответа ИИ-аналитики
 * (слайды 9–10): Краткий вывод → Отклонения → Причины → Связанные данные →
 * Рекомендуемые действия.
 *
 * Почему форма живёт здесь, а не в `Analytics.tsx`: экран `/analytics`
 * импортирует кейс, и обратный импорт замкнул бы два модуля в кольцо.
 * Зависимость односторонняя — кейс ничего не знает про станцию.
 *
 * Границы, которые экран держит осознанно:
 * — «Причины» собираются из дат и состояний записей, а не из суждения о поле;
 * — «Рекомендуемые действия» — это ссылки на действия прототипа с пометкой
 *   «решение за специалистом»; ни санкций, ни рейтингов, ни баллов;
 * — лабораторных анализов почвы в срезе нет ни одного, и разбор говорит это
 *   словами, а не печатает нули;
 * — правило севооборота в срезе не нормировано: повтор культуры остаётся
 *   наблюдением и не превращается в предписание.
 */

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Bug,
  ClipboardList,
  Droplets,
  FileCheck,
  FlaskConical,
  Layers,
  ListTree,
  MapPinned,
  Sprout,
  type LucideIcon,
} from 'lucide-react';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { Badge } from '../ui/Badge';
import { DataTable, type Column } from '../ui/DataTable';
import { DoneSplit } from '../ui/DoneSplit';
import { EmptyState } from '../ui/EmptyState';
import { EvidenceMark, type EvidenceClass } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { useTrackData } from '../data/useTrackData';
import { defineMessages, type PlainMessage } from '../../i18n/define';
import { useT, UNIT_LABELS } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateShort,
  ha,
  haUnit,
  m3,
  num,
  pct,
  share,
  WORDS,
} from '../lib/format';
import type {
  AttentionItem,
  ContourMeta,
  Crop,
  Cycle,
  DateWindow,
  Farm,
  Massif,
  Obligation,
  ObligationState,
  QuarantineFile,
  WaterFile,
  Work,
} from '../data/types';
import './pages.css';
import './analytics.css';

/* ------------------------------------------------------- форма ответа деки */

/**
 * Одно утверждение ответа. Эпистемический класс приходит ВМЕСТЕ с ним из
 * рецепта — экран его только печатает и не переклассифицирует.
 */
export interface Claim {
  text: ReactNode;
  evidence: EvidenceClass;
  evidenceLabel: string;
}

/** Модули прототипа, на которые ссылается блок «Связанные данные». */
export type ModuleId =
  | 'cycles'
  | 'farms'
  | 'contours'
  | 'map'
  | 'water'
  | 'quarantine'
  | 'verify'
  | 'assignments';

const MODULE_ROUTE: Record<ModuleId, { to: string; icon: LucideIcon }> = {
  cycles: { to: '/cycles', icon: ListTree },
  farms: { to: '/farms', icon: Building2 },
  contours: { to: '/contours', icon: Layers },
  map: { to: '/map', icon: MapPinned },
  water: { to: '/cycles', icon: Droplets },
  quarantine: { to: '/quarantine', icon: Bug },
  verify: { to: '/ops/verify', icon: FileCheck },
  assignments: { to: '/ops/assignments', icon: ClipboardList },
};

/** Шаг, который человек может сделать руками. Экран его не выполняет. */
export interface AnswerAction {
  label: string;
  /** Маршрут прототипа. Действие ведёт на экран, а не запускает команду. */
  to: string;
  /** Что откроется и по какому правилу собрана ссылка. */
  hint: string;
  icon: LucideIcon;
}

export interface Answer {
  title: string;
  /** Краткий вывод: одно-два предложения с главными числами. */
  summary: Claim;
  /** Отклонения: конкретные объекты с числами и ссылками. */
  deviations: Claim[];
  /** Что печатать, когда отклонений не нашлось, — словами и с причиной. */
  deviationsEmpty?: string;
  /** Причины: производные из дат и состояний записей. */
  causes: Claim[];
  /** Модули, к которым ответ относится. */
  modules: ModuleId[];
  /** Файлы среза, из которых собран ответ. */
  sources: string[];
  /** Как именно посчитано — словами, без «магии». */
  method: string;
  actions: AnswerAction[];
}

const M = defineMessages({
  /* --- форма ответа --- */
  derived: { id: 'contour.case.evidence.derived', defaultMessage: 'производный из среза', description: 'Метка эпистемического класса утверждения: значение выведено из данных среза; после неё идёт дата среза («производный из среза 26 авг 2026»)' },
  fact: { id: 'contour.case.evidence.fact', defaultMessage: 'факт среза', description: 'Метка эпистемического класса утверждения: значение прямо записано в срезе; после неё идёт дата среза' },
  formatMark: { id: 'contour.case.answer.formatMark', defaultMessage: 'формат ответа, модель не подключена', description: 'Метка в углу карточки ответа аналитики: это заготовка формата, языковая модель не подключена' },
  secSummary: { id: 'contour.case.answer.summary', defaultMessage: 'Краткий вывод', description: 'Заголовок раздела ответа аналитики: краткий вывод' },
  secDeviations: { id: 'contour.case.answer.deviations', defaultMessage: 'Отклонения', description: 'Заголовок раздела ответа аналитики: отклонения' },
  deviationsEmpty: { id: 'contour.case.answer.deviationsEmpty', defaultMessage: 'отклонений по этому вопросу в срезе не нашлось — это утверждение о данных, а не о поле', description: 'Текст раздела «Отклонения», когда отклонений нет и рецепт не дал своего текста' },
  secCauses: { id: 'contour.case.answer.causes', defaultMessage: 'Причины', description: 'Заголовок раздела ответа аналитики: причины' },
  causeCaveat: { id: 'contour.case.answer.causeCaveat', defaultMessage: 'Причина — производная от данных, а не суждение: срез знает даты и состояния записей и в поле не был. Названное здесь объясняет, почему так выглядят данные, и не утверждает, что произошло на земле.', description: 'Оговорка под разделом «Причины» ответа аналитики' },
  secRelated: { id: 'contour.case.answer.related', defaultMessage: 'Связанные данные', description: 'Заголовок раздела ответа аналитики: связанные разделы приложения и источники' },
  files: { id: 'contour.case.answer.files', defaultMessage: 'Файлы', description: 'Подпись строки в «Связанных данных»: файлы среза, из которых собран ответ' },
  trackAt: { id: 'contour.case.answer.trackAt', defaultMessage: 'трек данных на срезе {date}', description: 'Хвост строки «Файлы»: какой источник данных (трек) и на какую дату среза; {date} — короткая дата' },
  method: { id: 'contour.case.answer.method', defaultMessage: 'Метод', description: 'Подпись строки в «Связанных данных»: как посчитан ответ' },
  methodLine: { id: 'contour.case.answer.methodLine', defaultMessage: 'фиксированный рецепт: {method}', description: 'Значение строки «Метод»; {method} — описание рецепта словами' },
  limitation: { id: 'contour.case.answer.limitation', defaultMessage: 'Ограничение', description: 'Подпись строки в «Связанных данных»: ограничение ответа' },
  modelOff: { id: 'contour.case.answer.modelOff', defaultMessage: 'языковая модель не подключена — это заготовка формата ответа. Числа посчитаны на клиенте из файлов среза, поэтому ответ не знает ничего, чего нет в этих файлах, и не может ни запустить команду, ни изменить статус.', description: 'Текст строки «Ограничение»: модель не подключена, ответ — заготовка' },
  secActions: { id: 'contour.case.answer.actions', defaultMessage: 'Рекомендуемые действия', description: 'Заголовок раздела ответа аналитики: рекомендуемые действия' },
  actionCaveat: { id: 'contour.case.answer.actionCaveat', defaultMessage: 'рекомендация вспомогательная — решение за специалистом', description: 'Пометка у каждого рекомендуемого действия; коротко' },
  actionsNote: { id: 'contour.case.answer.actionsNote', defaultMessage: 'Ответ ничего не запускает: каждое действие — это переход на экран, где решение принимает человек. Санкций, рейтингов и баллов исполнительской дисциплины прототип не считает.', description: 'Пояснение под рекомендуемыми действиями' },

  modCycles: { id: 'contour.case.module.cycles.name', defaultMessage: 'Агроциклы', description: 'Ссылка в «Связанных данных»: раздел «Агроциклы»; коротко' },
  modCyclesHint: { id: 'contour.case.module.cycles.hint', defaultMessage: 'агроциклы, работы и окна среза', description: 'Подсказка к ссылке «Агроциклы»' },
  modFarms: { id: 'contour.case.module.farms.name', defaultMessage: 'Хозяйства', description: 'Ссылка в «Связанных данных»: раздел «Хозяйства»; коротко' },
  modFarmsHint: { id: 'contour.case.module.farms.hint', defaultMessage: 'реестр хозяйств района', description: 'Подсказка к ссылке «Хозяйства»' },
  modContours: { id: 'contour.case.module.contours.name', defaultMessage: 'Контуры', description: 'Ссылка в «Связанных данных»: раздел «Контуры»; коротко' },
  modContoursHint: { id: 'contour.case.module.contours.hint', defaultMessage: 'реестр контуров и размещение культур', description: 'Подсказка к ссылке «Контуры»' },
  modMap: { id: 'contour.case.module.map.name', defaultMessage: 'Карта', description: 'Ссылка в «Связанных данных»: раздел «Карта»; коротко' },
  modMapHint: { id: 'contour.case.module.map.hint', defaultMessage: 'слои: культуры, статус работ, карантин, геометрия', description: 'Подсказка к ссылке «Карта»' },
  modWater: { id: 'contour.case.module.water.name', defaultMessage: 'Вода', description: 'Ссылка в «Связанных данных»: вода; коротко' },
  modWaterHint: { id: 'contour.case.module.water.hint', defaultMessage: 'вода — срез внутри агроцикла: отдельного раздела в прототипе нет', description: 'Подсказка к ссылке «Вода»' },
  modQuarantine: { id: 'contour.case.module.quarantine.name', defaultMessage: 'Карантин', description: 'Ссылка в «Связанных данных»: раздел «Карантин»; коротко' },
  modQuarantineHint: { id: 'contour.case.module.quarantine.hint', defaultMessage: 'состояние района, обходы и справочник видов', description: 'Подсказка к ссылке «Карантин»' },
  modVerify: { id: 'contour.case.module.verify.name', defaultMessage: 'Очередь проверки', description: 'Ссылка в «Связанных данных»: раздел «Очередь проверки»; коротко' },
  modVerifyHint: { id: 'contour.case.module.verify.hint', defaultMessage: 'отчёты, которые ждут суждения проверяющего', description: 'Подсказка к ссылке «Очередь проверки»' },
  modAssignments: { id: 'contour.case.module.assignments.name', defaultMessage: 'Поручения', description: 'Ссылка в «Связанных данных»: раздел «Поручения» (адресные задания сотрудникам); коротко' },
  modAssignmentsHint: { id: 'contour.case.module.assignments.hint', defaultMessage: 'адресное задание со сроком и статусом', description: 'Подсказка к ссылке «Поручения»' },

  /* --- сезоны --- */
  seasonWinter: { id: 'contour.case.season.winter', defaultMessage: 'Озимый {year}–{next}', description: 'Подпись сезона размещения: озимый клин (посев осенью); {year} — год посева, {next} — две последние цифры следующего года' },
  seasonSpring: { id: 'contour.case.season.spring', defaultMessage: 'Сезон {year}', description: 'Подпись сезона размещения (яровой/основной сезон); {year} — год' },
  catArableIrr: { id: 'contour.case.category.arableIrr', defaultMessage: 'пашня орошаемая', description: 'Категория земли контура: орошаемая пашня' },
  catArableRain: { id: 'contour.case.category.arableRain', defaultMessage: 'пашня богарная', description: 'Категория земли контура: богарная пашня' },
  catOrchard: { id: 'contour.case.category.orchard', defaultMessage: 'сад', description: 'Категория земли контура: сад' },
  catVineyard: { id: 'contour.case.category.vineyard', defaultMessage: 'виноградник', description: 'Категория земли контура: виноградник' },
  catPasture: { id: 'contour.case.category.pasture', defaultMessage: 'пастбище', description: 'Категория земли контура: пастбище' },
  catOther: { id: 'contour.case.category.other', defaultMessage: 'прочее', description: 'Категория земли контура: прочее' },
  stateReported: { id: 'contour.case.state.reported', defaultMessage: 'отчитались', description: 'Состояние работы на контуре (бейдж в таблице): хозяйство отчиталось; коротко' },
  stateInWindow: { id: 'contour.case.state.inWindow', defaultMessage: 'окно идёт', description: 'Состояние работы на контуре (бейдж): окно идёт; коротко' },
  stateUpcoming: { id: 'contour.case.state.upcoming', defaultMessage: 'впереди', description: 'Состояние работы на контуре (бейдж): окно впереди; коротко' },
  stateSilent: { id: 'contour.case.state.silent', defaultMessage: 'без отчёта', description: 'Состояние работы на контуре (бейдж): отчёта нет; коротко' },
  late: { id: 'contour.case.state.late', defaultMessage: 'с опозданием', description: 'Бейдж у работы: отчёт пришёл после закрытия окна; коротко' },

  /* --- наблюдения --- */
  repeat: { id: 'contour.case.deviation.repeat', defaultMessage: '{crop} повторяется {gap, select, yes {через непроставленный сезон} other {два одноимённых сезона подряд}}: {from} и {to}. Повтор считается по одноимённым сезонам — яровой с яровым, озимый с озимым: озимый клин и яровая культура на одном контуре повтором не являются. Это наблюдение о повторе, а не нарушение: правило севооборота в срезе не нормировано, допустимая длина повтора нигде не задана — и предписания экран не выдаёт.', description: 'Отклонение в ответе аналитики по контуру: культура повторяется; {crop} — культура, {gap} — yes, если между записями есть непроставленный сезон, {from}/{to} — названия сезонов строчными' },
  silent: { id: 'contour.case.deviation.silent', defaultMessage: 'По закрытым окнам на контуре {count} без отчёта: <list></list>{hasMore, select, yes { и ещё {more}} other {}}. «Без отчёта» говорит, что запись не дошла, и не говорит, что работа не сделана.', description: 'Отклонение: на контуре есть работы без отчёта по закрытым окнам; {count} — число работ на полях со словом, <list> — список до трёх работ ссылками, {more} — сколько ещё' },
  silentItem: { id: 'contour.case.deviation.silentItem', defaultMessage: '<work>{name}</work> (окно закрылось {date})', description: 'Один пункт списка работ без отчёта; <work> — название работы ссылкой, {date} — дата закрытия окна' },
  lateReports: { id: 'contour.case.deviation.late', defaultMessage: '{late, plural, other {# из}} {reported, plural, one {# поступившего отчёта} other {# поступивших отчётов}} {late, plural, one {пришёл} other {пришли}} после закрытия своего окна. Канал работает, но с задержкой — это факт о датах записей.', description: 'Отклонение: часть отчётов пришла после закрытия окна; {late} — сколько позже срока, {reported} — всего поступивших отчётов' },
  watch: { id: 'contour.case.deviation.watch', defaultMessage: 'Карантин: под наблюдением с {date}. {hasCause, select, yes {Причина в журнале: {cause}.} other {Причина в журнале не названа.}} «Наблюдение» — статус обследования, а не приговор контуру.', description: 'Отклонение: карантинный статус «наблюдение»; {date} — с какой даты, {hasCause} — yes, если причина названа, {cause} — причина' },
  noPlacement: { id: 'contour.case.deviation.noPlacement', defaultMessage: 'Размещение на 2026 у контура не проставлено. Это пробел в данных, а не пар: пар в срезе — отдельная культура со своим кодом.', description: 'Отклонение: у контура нет размещения на 2026; «пар» — земля под парами' },
  candidateGeometry: { id: 'contour.case.deviation.candidate', defaultMessage: 'Геометрия контура — кандидат: привязка к номеру не подтверждена. Площадь ниже считается по реестру, а не по полигону.', description: 'Отклонение: геометрия контура не подтверждена' },
  candidateLabel: { id: 'contour.case.deviation.candidateLabel', defaultMessage: 'сопоставление не подтверждено', description: 'Метка эпистемического класса у отклонения про геометрию-кандидата' },
  causeSilent: { id: 'contour.case.cause.silent', defaultMessage: 'Молчание по работам — это отсутствие записи в срезе: окно закрылось раньше, чем пришёл отчёт. Кто и от чьего имени вносит отчёты, видно в <entry>экране ввода</entry>, а спорные — в <verify>очереди проверки</verify>.', description: 'Причина в ответе по контуру, когда есть работы без отчёта; <entry> и <verify> — ссылки на экраны ввода и очереди проверки' },
  causeNoDenominator: { id: 'contour.case.cause.noDenominator', defaultMessage: 'Долей отчётности у контура нет, потому что нет знаменателя: работ с открытыми окнами на нём не заведено. Молчание и опоздание — состояния записи, а записей по этому контуру агроциклы не порождали.', description: 'Причина в ответе по контуру, когда у контура нет работ с открытыми окнами' },
  causeNoSilence: { id: 'contour.case.cause.noSilence', defaultMessage: 'Молчания по закрытым окнам на контуре нет: на все открытые окна ({count}) пришёл отчёт.', description: 'Причина в ответе по контуру: молчания нет; {count} — число работ на полях со словом' },
  causeDepthDeep: { id: 'contour.case.cause.depthDeep', defaultMessage: 'Глубина истории размещения — {seasons} среза: на такой глубине повтор культуры уже видно. Но допустимая длина повтора нигде не задана — правило севооборота в срезе не нормировано, поэтому наблюдение остаётся наблюдением, а не нарушением.', description: 'Причина в ответе по контуру: история размещения достаточно глубока; {seasons} — число сезонов со словом' },
  causeDepthShallow: { id: 'contour.case.cause.depthShallow', defaultMessage: 'Глубина истории размещения — {seasons} среза. На такой глубине чередование культур не проверяется всерьёз: вывод о севообороте требует нескольких лет подряд, а срез их не содержит.', description: 'Причина в ответе по контуру: история размещения короткая; {seasons} — число сезонов со словом' },
  causeLab: { id: 'contour.case.cause.lab', defaultMessage: 'Лабораторных анализов почвы по контуру не поступало: ни K, ни органики, ни pH, ни N/P/K — в срезе нет ни одного параметра и ни одной даты отбора. Агрономической причины разбор поэтому не называет: отсутствие анализа — это отсутствие, а не нулевое значение.', description: 'Причина в ответе по контуру: лабораторных анализов почвы нет' },
  causeLabLabel: { id: 'contour.case.cause.labLabel', defaultMessage: 'факт среза: лабораторных данных нет', description: 'Метка эпистемического класса у причины про отсутствие лабораторных данных' },
  actOpenContour: { id: 'contour.case.action.openContour', defaultMessage: 'Открыть карточку контура', description: 'Рекомендуемое действие: открыть карточку контура' },
  actOpenContourHint: { id: 'contour.case.action.openContourHint', defaultMessage: 'паспорт, землепользование, журнал карантина и происхождение геометрии', description: 'Подсказка к действию «Открыть карточку контура»' },
  actAssign: { id: 'contour.case.action.assign', defaultMessage: 'Создать поручение по контуру', description: 'Рекомендуемое действие: создать адресное поручение сотруднику по контуру' },
  actAssignHintFrom: { id: 'contour.case.action.assignHintFrom', defaultMessage: 'адресное задание со сроком и статусом; статус и срок там ведутся отдельно. Позиция очереди: {title} — подставит заголовок, описание и привязку', description: 'Подсказка к действию «Создать поручение», когда есть позиция очереди внимания; {title} — её заголовок' },
  actAssignHintNone: { id: 'contour.case.action.assignHintNone', defaultMessage: 'адресное задание со сроком и статусом; статус и срок там ведутся отдельно. Позиции очереди по этому контуру в срезе нет — форма откроется пустой, предмет придётся назвать словами: контур № {no}', description: 'Подсказка к действию «Создать поручение», когда позиции очереди нет; {no} — номер контура' },
  actAssignHintNoneFarm: { id: 'contour.case.action.assignHintNoneFarm', defaultMessage: 'адресное задание со сроком и статусом; статус и срок там ведутся отдельно. Позиции очереди по этому контуру в срезе нет — форма откроется пустой, предмет придётся назвать словами: контур № {no}, {farm}', description: 'То же, когда известно хозяйство; {no} — номер контура, {farm} — название хозяйства' },
  actOpenWork: { id: 'contour.case.action.openWork', defaultMessage: 'Открыть работу «{name}»', description: 'Рекомендуемое действие: открыть работу без отчёта; {name} — название работы' },
  actOpenWorkHint: { id: 'contour.case.action.openWorkHint', defaultMessage: 'работа, по которой на этом контуре нет отчёта: две оси и разбивка по хозяйствам', description: 'Подсказка к действию «Открыть работу»' },
  actOpenFarm: { id: 'contour.case.action.openFarm', defaultMessage: 'Открыть хозяйство {name}', description: 'Рекомендуемое действие: открыть хозяйство; {name} — название хозяйства' },
  actOpenFarmHint: { id: 'contour.case.action.openFarmHint', defaultMessage: 'земля, агроциклы, две оси работ и вода хозяйства целиком', description: 'Подсказка к действию «Открыть хозяйство»' },
  answerTitle: { id: 'contour.case.answer.title', defaultMessage: 'Наблюдения по контуру № {no}', description: 'Заголовок карточки ответа аналитики по контуру; {no} — номер контура' },
  summaryNoWorks: { id: 'contour.case.summary.noWorks', defaultMessage: 'Контур № {no} — {area}, {category}, хозяйство <farm></farm>; размещение 2026 — {crop}. Работ на контуре в срезе нет: ни один агроцикл не назначил ему работ, поэтому долей отчётности у контура тоже нет. Глубина истории размещения — {seasons}.', description: 'Краткий вывод по контуру без работ; {area} — площадь с единицей, {category} — категория земли, <farm> — хозяйство ссылкой, {crop} — культура 2026 или «не проставлено», {seasons} — число сезонов со словом' },
  summaryWorks: { id: 'contour.case.summary.works', defaultMessage: 'Контур № {no} — {area}, {category}, хозяйство <farm></farm>; размещение 2026 — {crop}. По агроциклам среза на контуре открытых окон: {eligible}; отчитались {reported}, без отчёта {silent}; глубина истории размещения — {seasons}.', description: 'Краткий вывод по контуру с работами; {eligible} — число работ на полях с открытыми окнами со словом, {reported}/{silent} — сколько с отчётом и без; остальное как в выводе без работ' },
  notPlaced: { id: 'contour.case.crop.notPlaced', defaultMessage: 'не проставлено', description: 'Культура в клетке или выводе, когда размещение не проставлено' },
  method2: { id: 'contour.case.answer.methodText', defaultMessage: 'работы контура из обоих агроциклов, сезоны размещения из всех полей crop_* реестра контуров, статус и журнал карантина по контуру', description: 'Описание метода в ответе аналитики по контуру; crop_* — служебные поля реестра, переводить не нужно' },
  deviationsNone: { id: 'contour.case.answer.deviationsNone', defaultMessage: 'по контуру не нашлось ни повтора культуры, ни молчания по закрытым окнам, ни статуса наблюдения. Это утверждение о записях среза, а не о поле', description: 'Текст раздела «Отклонения», когда у контура ничего не найдено' },

  /* --- экран --- */
  loadingRegistry: { id: 'contour.case.loading', defaultMessage: 'Загружаем реестр контуров…', description: 'Текст загрузки в карточке разбора контура' },
  caseTitle: { id: 'contour.case.card.title', defaultMessage: 'Разбор контура', description: 'Заголовок карточки разбора контура (пока контур загружается или не найден)' },
  notFoundTitle: { id: 'contour.case.notFound.title', defaultMessage: 'Контура с таким адресом в срезе нет', description: 'Заголовок пустого состояния: контур по адресу не найден' },
  notFoundReason: { id: 'contour.case.notFound.reason', defaultMessage: 'Реестр контуров прочитан, но контура «{id}» в нём не оказалось. Паспорт, история культур и наблюдения не показываются: говорить про несуществующий контур нечего.', description: 'Пояснение к пустому состоянию «контур не найден»; {id} — адрес' },
  colWork: { id: 'contour.case.works.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы работ контура: название работы' },
  colCycle: { id: 'contour.case.works.col.cycle', defaultMessage: 'Агроцикл', description: 'Колонка таблицы работ контура: агроцикл; узкая колонка' },
  colWindow: { id: 'contour.case.works.col.window', defaultMessage: 'Окно', description: 'Колонка таблицы работ контура: окно дат; узкая колонка' },
  colState: { id: 'contour.case.works.col.state', defaultMessage: 'Состояние', description: 'Колонка таблицы работ контура: состояние работы' },
  contourTitle: { id: 'contour.case.passport.title', defaultMessage: 'Контур № {no}', description: 'Заголовок карточки паспорта в разборе контура; {no} — номер' },
  noGeometry: { id: 'contour.case.passport.noGeometry', defaultMessage: 'Геометрии нет', description: 'Бейдж в карточке паспорта: геометрии нет; коротко' },
  geometryCandidate: { id: 'contour.case.passport.geometryCandidate', defaultMessage: 'геометрия — кандидат', description: 'Метка в карточке паспорта: геометрия не подтверждена' },
  geometryAccepted: { id: 'contour.case.passport.geometryAccepted', defaultMessage: 'геометрия принята (производная привязка NGIS)', description: 'Метка в карточке паспорта: геометрия принята; NGIS — название источника привязки' },
  kpiArea: { id: 'contour.case.kpi.area', defaultMessage: 'Площадь', description: 'Подпись KPI разбора контура: площадь, га земли' },
  kpiAreaNote: { id: 'contour.case.kpi.areaNote', defaultMessage: 'по реестру среза', description: 'Мелкая подпись под KPI «Площадь»' },
  kpiCategory: { id: 'contour.case.kpi.category', defaultMessage: 'Категория', description: 'Подпись KPI разбора контура: категория земли' },
  kpiCategoryNote: { id: 'contour.case.kpi.categoryNote', defaultMessage: 'землепользование контура', description: 'Мелкая подпись под KPI «Категория»' },
  kpiFarm: { id: 'contour.case.kpi.farm', defaultMessage: 'Хозяйство', description: 'Подпись KPI разбора контура: хозяйство-землепользователь' },
  kpiFarmDirection: { id: 'contour.case.kpi.farmDirection', defaultMessage: 'направление: {direction}', description: 'Мелкая подпись под KPI «Хозяйство»; {direction} — направление хозяйства (хлопок, зерно…)' },
  kpiFarmUnread: { id: 'contour.case.kpi.farmUnread', defaultMessage: 'название хозяйства не прочитано', description: 'Мелкая подпись под KPI «Хозяйство», когда название не удалось прочитать' },
  kpiMassif: { id: 'contour.case.kpi.massif', defaultMessage: 'Массив', description: 'Подпись KPI разбора контура: массив' },
  kpiMassifNote: { id: 'contour.case.kpi.massifNote', defaultMessage: 'уровень выше по цепочке', description: 'Мелкая подпись под KPI «Массив»' },
  slidesNote: { id: 'contour.case.passport.slidesNote', defaultMessage: 'Кейс контура из презентации (слайды 14–15) опирался на пятилетнюю историю культур и лабораторный анализ почвы. В срезе — {seasons} истории и ни одного анализа: ниже показано ровно то, что есть, и названо то, чего нет.', description: 'Пояснение под KPI в разборе контура; {seasons} — число сезонов со словом' },
  historyTitle: { id: 'contour.case.history.title', defaultMessage: 'История культур', description: 'Заголовок карточки истории культур по сезонам' },
  historyEmptyTitle: { id: 'contour.case.history.empty.title', defaultMessage: 'Сезонов размещения в реестре нет', description: 'Заголовок пустого состояния истории культур' },
  historyEmptyReason: { id: 'contour.case.history.empty.reason', defaultMessage: 'в реестре контуров не нашлось ни одного сезонного поля размещения — история культур не собирается', description: 'Пояснение к пустому состоянию истории культур' },
  railNote: { id: 'contour.case.history.railNote', defaultMessage: 'запись реестра контуров', description: 'Мелкая подпись под культурой в ленте сезонов: запись есть в реестре' },
  railNoNote: { id: 'contour.case.history.railNoNote', defaultMessage: 'записи в срезе нет — это не пар', description: 'Мелкая подпись под клеткой сезона без записи; «пар» — земля под парами' },
  depthNote: { id: 'contour.case.history.depthNote', defaultMessage: 'Глубина истории — {seasons} среза; сезоны читаются из имён полей реестра, поэтому новый сезон появляется здесь сам, без правки экрана.', description: 'Пояснение под лентой сезонов: глубина истории; {seasons} — число сезонов со словом' },
  repeatsSome: { id: 'contour.case.history.repeatsSome', defaultMessage: 'Повторов культуры по одноимённым сезонам: {count} ({list}). Правило севооборота в срезе не нормировано — это наблюдение, а не предписание.', description: 'Пояснение под лентой сезонов: найдены повторы; {count} — сколько повторов, {list} — перечень «культура: сезон и сезон» через «; »' },
  repeatsItem: { id: 'contour.case.history.repeatsItem', defaultMessage: '{crop}: {from} и {to}', description: 'Один повтор в перечне; {crop} — культура, {from}/{to} — названия сезонов строчными' },
  repeatsNone: { id: 'contour.case.history.repeatsNone', defaultMessage: 'Повтора культуры по одноимённым сезонам в этих сезонах нет: яровой сравнивается с яровым, озимый — с озимым, и озимый клин рядом с яровой культурой повтором не считается.', description: 'Пояснение под лентой сезонов: повторов нет' },
  worksTitle: { id: 'contour.case.works.title', defaultMessage: 'Работы контура', description: 'Заголовок карточки работ контура в разборе' },
  worksBothCycles: { id: 'contour.case.works.bothCycles', defaultMessage: 'оба агроцикла среза', description: 'Бейдж у заголовка «Работы контура»: учтены два агроцикла среза; коротко' },
  worksLoading: { id: 'contour.case.works.loading', defaultMessage: 'Загружаем работы по контуру…', description: 'Текст загрузки в карточке работ контура' },
  worksMissingTitle: { id: 'contour.case.works.missing.title', defaultMessage: 'Работы не загружены', description: 'Заголовок пустого состояния: данные работ не прочитались' },
  worksMissingReason: { id: 'contour.case.works.missing.reason', defaultMessage: 'данные работ ни одного агроцикла не прочитались в текущем треке', description: 'Пояснение: данные работ не прочитались; «трек» — источник данных' },
  allAhead: { id: 'contour.case.works.allAhead', defaultMessage: 'Все {count} контура относятся к работам впереди: окна ещё не открылись, и доли по ним не считаются — это «ещё не срок», а не «не сделано».', description: 'Сообщение вместо полосы выполнения, когда все окна впереди; {count} — число работ на полях со словом' },
  doneDenominator: { id: 'contour.case.works.denominator', defaultMessage: '{count, plural, one {работа контура} few {работы контура} many {работ контура} other {работы контура}} в открытых окнах', description: 'Слово-знаменатель под полосой выполнения: «5 из 12 работ контура в открытых окнах»; без числа' },
  upcomingNote: { id: 'contour.case.works.upcomingNote', defaultMessage: 'ещё {count} впереди и в знаменатель не входят: у них не «не сделано», а «ещё не срок»', description: 'Пояснение под полосой выполнения; {count} — сколько работ впереди (число)' },
  worksUnit: { id: 'contour.case.works.table.denominator', defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}', description: 'Слово-знаменатель под таблицей работ контура: «12 работ на полях»; без числа' },
  worksEmptyTitle: { id: 'contour.case.works.table.empty.title', defaultMessage: 'Работ на контуре нет', description: 'Заголовок пустой таблицы работ контура' },
  worksEmptyReason: { id: 'contour.case.works.table.empty.reason', defaultMessage: 'ни один агроцикл среза не назначил работу этому контуру', description: 'Пояснение к пустой таблице работ контура' },
  waterTitle: { id: 'contour.case.water.title', defaultMessage: 'Вода', description: 'Заголовок карточки воды в разборе контура' },
  waterLoading: { id: 'contour.case.water.loading', defaultMessage: 'Загружаем заявки на воду…', description: 'Текст загрузки в карточке воды' },
  waterEmptyTitle: { id: 'contour.case.water.empty.title', defaultMessage: 'Заявок на воду не нашлось', description: 'Заголовок пустого состояния карточки воды' },
  waterEmptyReason: { id: 'contour.case.water.empty.reason', defaultMessage: 'в данных воды нет ни одной строки по хозяйству этого контура — это отсутствие записей, а не нулевая подача', description: 'Пояснение к пустому состоянию карточки воды' },
  waterRequested: { id: 'contour.case.water.requested', defaultMessage: 'Запрошено', description: 'Подпись строки карточки воды: запрошенный объём' },
  waterDelivered: { id: 'contour.case.water.delivered', defaultMessage: 'Подано', description: 'Подпись строки карточки воды: поданный объём' },
  waterCoverage: { id: 'contour.case.water.coverage', defaultMessage: 'Покрытие', description: 'Подпись строки карточки воды: доля поданного от запрошенного' },
  waterRequests: { id: 'contour.case.water.requests', defaultMessage: 'Заявок', description: 'Подпись строки карточки воды: число заявок' },
  waterRequestsLine: { id: 'contour.case.water.requestsLine', defaultMessage: '{count} · частично {partial} · ждут {pending}', description: 'Значение строки «Заявок»; {count} — всего, {partial} — подано частично, {pending} — ждут подачи; числа отформатированы' },
  waterNote: { id: 'contour.case.water.note', defaultMessage: 'Заявка на воду в срезе привязана к паре «хозяйство × работа», а не к контуру. Это вода всего хозяйства {farm} по обоим агроциклам: долю именно этого контура срез не хранит, и делить её по гектарам экран не станет.', description: 'Пояснение под цифрами воды; {farm} — название хозяйства' },
  qTitle: { id: 'contour.case.quarantine.title', defaultMessage: 'Карантин', description: 'Заголовок карточки карантина в разборе контура' },
  qWatch: { id: 'contour.case.quarantine.watch', defaultMessage: 'Под наблюдением с {date}. {hasCause, select, yes {Причина: {cause}.} other {Причина не названа.}}', description: 'Строка карантина: наблюдение; {date} — с какой даты, {hasCause} — yes, если причина названа, {cause} — причина' },
  qGreen: { id: 'contour.case.quarantine.green', defaultMessage: 'Зелёный с {date}: отдельная запись подтверждена в срезе.', description: 'Строка карантина: зелёный статус с записью; {date} — с какой даты' },
  qGreenDefault: { id: 'contour.case.quarantine.greenDefault', defaultMessage: 'Зелёный по умолчанию: отдельной записи по контуру в срезе нет. Это оговорка о данных, а не результат обследования.', description: 'Строка карантина: зелёный по умолчанию' },
  inspClean: { id: 'contour.case.quarantine.inspClean', defaultMessage: 'без находок', description: 'Строка обследования: ничего не найдено' },
  inspFinding: { id: 'contour.case.quarantine.inspFinding', defaultMessage: '{kind}: {name}', description: 'Строка обследования с находкой; {kind} — вид («вредитель»), {name} — название' },
  inspFindingMeasure: { id: 'contour.case.quarantine.inspFindingMeasure', defaultMessage: '{kind}: {name} — {measure}', description: 'Строка обследования с находкой и мерой; {measure} — размер поражения' },
  inspKindFallback: { id: 'contour.case.quarantine.kindFallback', defaultMessage: 'находка', description: 'Подстановка вместо вида находки, когда он не назван' },
  inspNone: { id: 'contour.case.quarantine.inspNone', defaultMessage: 'Обследований по этому контуру в срезе нет: молчание журнала ничего не утверждает о поле.', description: 'Пустое состояние списка обследований контура' },
  labTitle: { id: 'contour.case.lab.title', defaultMessage: 'Лабораторные анализы почвы', description: 'Заголовок карточки лабораторных анализов почвы' },
  labEmptyTitle: { id: 'contour.case.lab.empty.title', defaultMessage: 'Лабораторные данные не поступали', description: 'Заголовок пустого состояния карточки лабораторных анализов' },
  labEmptyReason: { id: 'contour.case.lab.empty.reason', defaultMessage: 'в срезе нет ни одного анализа почвы: ни K, ни органики, ни pH, ни N/P/K, ни Ca/Mg, ни B/Zn, ни даты отбора. На слайде 14 разбор опирался именно на них — здесь их нет, и подставить нечего', description: 'Пояснение к пустому состоянию лабораторных анализов' },
});

/** Метки эпистемических классов: слова — сообщения, дата среза добавляется при выдаче. */
export const DERIVED = { toString: (): string => tr(M.derived) };
export const FACT = { toString: (): string => tr(M.fact) };

const MODULE_MSG = {
  cycles: { name: M.modCycles, hint: M.modCyclesHint },
  farms: { name: M.modFarms, hint: M.modFarmsHint },
  contours: { name: M.modContours, hint: M.modContoursHint },
  map: { name: M.modMap, hint: M.modMapHint },
  water: { name: M.modWater, hint: M.modWaterHint },
  quarantine: { name: M.modQuarantine, hint: M.modQuarantineHint },
  verify: { name: M.modVerify, hint: M.modVerifyHint },
  assignments: { name: M.modAssignments, hint: M.modAssignmentsHint },
} as const;

const CATEGORY_MSG = {
  arable_irr: M.catArableIrr,
  arable_rain: M.catArableRain,
  orchard: M.catOrchard,
  vineyard: M.catVineyard,
  pasture: M.catPasture,
  other: M.catOther,
} as const;

const STATE_MSG: Record<ObligationState, PlainMessage> = {
  reported_verified: M.stateReported,
  reported: M.stateReported,
  in_window: M.stateInWindow,
  upcoming: M.stateUpcoming,
  silent: M.stateSilent,
};

const categoryOf = (category: string): string =>
  category in CATEGORY_MSG ? tr(CATEGORY_MSG[category as keyof typeof CATEGORY_MSG]) : category;

function ClaimRow({ claim }: { claim: Claim }) {
  return (
    <div className="ans__claim">
      <p className="ans__claim-text">{claim.text}</p>
      {/* Класс выдан рецептом вместе с утверждением; повысить его экран не может. */}
      <EvidenceMark kind={claim.evidence} label={claim.evidenceLabel} />
    </div>
  );
}

/**
 * Пять именованных блоков в фиксированном порядке — ровно тот, что заказчик
 * видел на слайдах 9–10. Порядок задан разметкой: рецепт не может переставить
 * секции и не может выкинуть «Связанные данные».
 */
export function AnswerView({ answer, asOf }: { answer: Answer; asOf: string }) {
  const t = useT();
  return (
    <Card size="lg" title={answer.title} aside={<EvidenceMark kind="demo" label={t(M.formatMark)} />}>
      <div className="ans">
        <section className="ans__sec">
          <div className="ans__label">{t(M.secSummary)}</div>
          <p className="ans__lead">{answer.summary.text}</p>
          <EvidenceMark kind={answer.summary.evidence} label={answer.summary.evidenceLabel} />
        </section>

        <section className="ans__sec">
          <div className="ans__label">{t(M.secDeviations)}</div>
          {answer.deviations.length === 0 ? (
            <p className="t-caption">{answer.deviationsEmpty ?? t(M.deviationsEmpty)}</p>
          ) : (
            <div className="ans__claims">
              {answer.deviations.map((claim, i) => (
                <ClaimRow claim={claim} key={i} />
              ))}
            </div>
          )}
        </section>

        <section className="ans__sec">
          <div className="ans__label">{t(M.secCauses)}</div>
          <div className="ans__claims">
            {answer.causes.map((claim, i) => (
              <ClaimRow claim={claim} key={i} />
            ))}
          </div>
          <p className="ans__note">{t(M.causeCaveat)}</p>
        </section>

        <section className="ans__sec">
          <div className="ans__label">{t(M.secRelated)}</div>
          <div className="ans__mods">
            {answer.modules.map((id) => {
              const route = MODULE_ROUTE[id];
              return (
                <Link className="ans__mod focus-ring" key={id} to={route.to} title={t(MODULE_MSG[id].hint)}>
                  <Icon icon={route.icon} size={16} />
                  {t(MODULE_MSG[id].name)}
                </Link>
              );
            })}
          </div>
          <div className="ans__data">
            <div className="ans__data-row">
              <span className="ans__data-key">{t(M.files)}</span>
              <span className="ans__data-val">
                {answer.sources.map((file, i) => (
                  <span key={file}>
                    {i > 0 ? ', ' : ''}
                    <code>{file}</code>
                  </span>
                ))}{' '}
                · {t(M.trackAt, { date: dateShort(asOf) })}
              </span>
            </div>
            <div className="ans__data-row">
              <span className="ans__data-key">{t(M.method)}</span>
              <span className="ans__data-val">{t(M.methodLine, { method: answer.method })}</span>
            </div>
            <div className="ans__data-row">
              <span className="ans__data-key">{t(M.limitation)}</span>
              <span className="ans__data-val">{t(M.modelOff)}</span>
            </div>
          </div>
        </section>

        <section className="ans__sec">
          <div className="ans__label">{t(M.secActions)}</div>
          <div className="ans__acts">
            {answer.actions.map((action) => (
              <Link className="ans__act focus-ring" key={action.to + action.label} to={action.to}>
                <span className="ans__act-icon" aria-hidden="true">
                  <Icon icon={action.icon} size={18} />
                </span>
                <span className="ans__act-label">{action.label}</span>
                <span className="ans__act-hint">{action.hint}</span>
                {/* Пометка стоит у КАЖДОГО шага, а не одна на блок: ссылка
                    уезжает в другой экран вместе со своей оговоркой. */}
                <span className="ans__act-mark">{t(M.actionCaveat)}</span>
              </Link>
            ))}
          </div>
          <p className="ans__note">{t(M.actionsNote)}</p>
        </section>
      </div>
    </Card>
  );
}

/* --------------------------------------------------------- сезоны контура */

/**
 * Сезон размещения читается из ИМЕНИ поля `contours.meta.json`, а не из
 * зашитого списка: если генератор добавит `crop_2024w` или `crop_2025`,
 * колонка появится сама. Порядок — хронологический: озимый сезон года N идёт
 * после ярового того же года, потому что сеется осенью.
 */
const SEASON_KEY = /^crop_(\d{4})(w)?$/;

export interface SeasonCol {
  key: string;
  year: number;
  winter: boolean;
  label: string;
  order: number;
}

export function seasonsOf(contour: ContourMeta): SeasonCol[] {
  const out: SeasonCol[] = [];
  for (const key of Object.keys(contour)) {
    const hit = SEASON_KEY.exec(key);
    if (!hit) continue;
    const year = Number(hit[1]);
    if (!Number.isFinite(year)) continue;
    const winter = hit[2] === 'w';
    out.push({
      key,
      year,
      winter,
      label: winter ? tr(M.seasonWinter, { year, next: String(year + 1).slice(2) }) : tr(M.seasonSpring, { year }),
      order: year * 2 + (winter ? 1 : 0),
    });
  }
  return out.sort((a, b) => a.order - b.order);
}

/** Значение сезонного поля. Пустая строка читается как отсутствие записи. */
export function cropAt(contour: ContourMeta, key: string): string | null {
  const value = (contour as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Пар — это отдельная культура среза, а не «пусто»; в повторе он не участвует. */
const FALLOW = 'fallow';

/**
 * Сколько позиций очереди внимания форма поручения показывает списком
 * источников (`SOURCE_LIMIT` на `/ops/assignments`). Позиция ниже границы
 * подставит заголовок, но не привязку, — предметом она служить не может.
 */
const ATTENTION_SOURCE_LIMIT = 10;

interface Repeat {
  crop: string;
  from: SeasonCol;
  to: SeasonCol;
  /** Между записями остались непроставленные сезоны того же типа. */
  gap: boolean;
}

/**
 * Повтор считается по ОДНОИМЁННЫМ сезонам: яровой сравнивается с яровым,
 * озимый — с озимым. В общем хронологическом порядке соседями оказываются
 * разные слоты севооборота (озимый клин и яровая культура на одном контуре —
 * норма), и сравнение «подряд по списку» объявляло бы повтором то, чем он не
 * является, а настоящий повтор — хлопок по хлопку через год — не видело вовсе.
 *
 * Непроставленный сезон цепочку не разрывает: пробел — это отсутствие записи,
 * а не другая культура. Такой повтор помечается `gap`, и текст называет его
 * повтором через пропуск, а не «подряд».
 */
function repeatsOf(contour: ContourMeta, seasons: SeasonCol[]): Repeat[] {
  const out: Repeat[] = [];

  for (const winter of [false, true]) {
    const chain = seasons.filter((s) => s.winter === winter);
    let prev: { season: SeasonCol; crop: string } | null = null;
    let skipped = false;

    for (const season of chain) {
      const crop = cropAt(contour, season.key);
      if (!crop) {
        if (prev) skipped = true;
        continue;
      }
      if (prev && prev.crop === crop && crop !== FALLOW) {
        out.push({ crop, from: prev.season, to: season, gap: skipped });
      }
      prev = { season, crop };
      skipped = false;
    }
  }

  return out.sort((a, b) => a.to.order - b.to.order);
}

/* ------------------------------------------------------------------- экран */

interface CaseWorkRow {
  key: string;
  cycleId: string;
  cycleLabel: string;
  workId: string;
  workName: string;
  window: DateWindow;
  state: ObligationState;
  late: boolean;
}

function rowsOfCycle(
  cycle: Cycle | null,
  label: string,
  obligations: Obligation[] | null,
  works: Work[] | null,
  contourId: string,
): CaseWorkRow[] {
  if (!cycle || !obligations || !works) return [];
  const workById = new Map(works.map((w) => [w.id, w]));
  return obligations
    .filter((o) => o.contour_id === contourId)
    .map((o) => {
      const work = workById.get(o.work_id);
      return {
        key: o.id,
        cycleId: cycle.id,
        cycleLabel: label,
        workId: o.work_id,
        workName: work?.name ?? o.work_id,
        window: work?.window ?? { from: '', to: '' },
        state: o.state,
        late: o.late,
      };
    })
    .sort((a, b) => a.window.from.localeCompare(b.window.from));
}

export function CaseContour({ contourId, asOf }: { contourId: string; asOf: string }) {
  const t = useT();
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const attention = useTrackData<AttentionItem[]>(FILES.attention);

  /*
   * Разбор работ ведётся по циклам ТЕКУЩЕГО сезона: активный + последний
   * закрытый. Исторические циклы («экономная глубина») обязательств не несут —
   * взяв самые ранние по окну, экран молча сравнивал бы с пустотой.
   */
  const cycleList = useMemo(() => {
    const all = [...(cycles.data ?? [])].sort((a, b) =>
      b.window.from.localeCompare(a.window.from),
    );
    const active = all.filter((c) => c.status === 'active');
    const closed = all.filter((c) => c.status !== 'active');
    return [...active, ...closed].slice(0, 2).sort((a, b) =>
      a.window.from.localeCompare(b.window.from),
    );
  }, [cycles.data]);
  const cycleA = cycleList[0] ?? null;
  const cycleB = cycleList[1] ?? null;

  const worksA = useTrackData<Work[]>(cycleA ? FILES.works(cycleA.id) : null);
  const worksB = useTrackData<Work[]>(cycleB ? FILES.works(cycleB.id) : null);
  const obligA = useTrackData<Obligation[]>(cycleA ? FILES.obligations(cycleA.id) : null);
  const obligB = useTrackData<Obligation[]>(cycleB ? FILES.obligations(cycleB.id) : null);
  const waterA = useTrackData<WaterFile>(cycleA ? FILES.water(cycleA.id) : null);
  const waterB = useTrackData<WaterFile>(cycleB ? FILES.water(cycleB.id) : null);

  const contour = (contours.data ?? []).find((c) => c.id === contourId) ?? null;
  const farm = contour ? (farms.data ?? []).find((f) => f.id === contour.farm_id) ?? null : null;
  const massif = contour
    ? (massifs.data ?? []).find((m) => m.id === contour.massif_id) ?? null
    : null;

  const cropName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crops.data ?? []) map.set(c.id, c.name);
    return map;
  }, [crops.data]);

  const labelA = cycleA ? cycleTitle(cycleA, crops.data) : '—';
  const labelB = cycleB ? cycleTitle(cycleB, crops.data) : '—';

  const workRows = useMemo(() => {
    const a = rowsOfCycle(cycleA, labelA, obligA.data, worksA.data, contourId);
    const b = rowsOfCycle(cycleB, labelB, obligB.data, worksB.data, contourId);
    return [...a, ...b].sort((x, y) => x.window.from.localeCompare(y.window.from));
  }, [cycleA, cycleB, labelA, labelB, obligA.data, obligB.data, worksA.data, worksB.data, contourId]);

  const worksLoading =
    obligA.state === 'loading' ||
    obligB.state === 'loading' ||
    worksA.state === 'loading' ||
    worksB.state === 'loading';
  const worksReadable = obligA.data !== null || obligB.data !== null;

  const tally = useMemo(() => {
    const eligible = workRows.filter((r) => r.state !== 'upcoming');
    const reported = eligible.filter(
      (r) => r.state === 'reported' || r.state === 'reported_verified',
    );
    return {
      all: workRows.length,
      eligible: eligible.length,
      upcoming: workRows.length - eligible.length,
      reported: reported.length,
      verified: eligible.filter((r) => r.state === 'reported_verified').length,
      silent: eligible.filter((r) => r.state === 'silent').length,
      inWindow: eligible.filter((r) => r.state === 'in_window').length,
      late: reported.filter((r) => r.late).length,
    };
  }, [workRows]);

  const silentRows = useMemo(() => workRows.filter((r) => r.state === 'silent'), [workRows]);

  const seasons = useMemo(() => (contour ? seasonsOf(contour) : []), [contour]);
  const repeats = useMemo(
    () => (contour ? repeatsOf(contour, seasons) : []),
    [contour, seasons],
  );

  /*
   * Вода в срезе привязана к паре «хозяйство × работа», а не к контуру:
   * доля контура из поданного объёма нигде не записана и здесь не выдумывается.
   */
  const water = useMemo(() => {
    if (!contour) return null;
    const rows = [...(waterA.data?.rows ?? []), ...(waterB.data?.rows ?? [])].filter(
      (r) => r.farm_id === contour.farm_id,
    );
    if (rows.length === 0) return null;
    return {
      count: rows.length,
      requested: rows.reduce((s, r) => s + r.requested_m3, 0),
      delivered: rows.reduce((s, r) => s + r.delivered_m3, 0),
      pending: rows.filter((r) => r.status === 'pending').length,
      partial: rows.filter((r) => r.status === 'partial').length,
    };
  }, [contour, waterA.data, waterB.data]);

  const waterLoading = waterA.state === 'loading' || waterB.state === 'loading';

  const qStatus = (quarantine.data?.statuses ?? []).find((s) => s.contour_id === contourId) ?? null;
  const qInspections = useMemo(
    () => (quarantine.data?.inspections ?? []).filter((i) => i.contour_id === contourId),
    [quarantine.data, contourId],
  );

  /**
   * Позиция очереди внимания по ЭТОМУ контуру — предмет для формы поручения.
   * Ограничение по рангу совпадает с `SOURCE_LIMIT` на `/ops/assignments`:
   * позиция ниже границы подставила бы заголовок, но не привязку.
   */
  const fromAttention = useMemo(() => {
    if (!contourId) return null;
    return (
      [...(attention.data ?? [])]
        .sort((a, b) => a.rank - b.rank)
        .slice(0, ATTENTION_SOURCE_LIMIT)
        .find((item) => item.ref.contour_id === contourId) ?? null
    );
  }, [attention.data, contourId]);

  /* ------------------------------------------------------------ наблюдения */

  const answer: Answer | null = useMemo(() => {
    if (!contour) return null;
    const farmName = farm?.name ?? contour.farm_id;
    const farmLink = (chunks: ReactNode) => <Link to={`/farms/${contour.farm_id}`}>{chunks}</Link>;
    const placed = cropAt(contour, 'crop_2026');
    const cropOfPlaced = placed ? cropName.get(placed) ?? placed : t(M.notPlaced);
    const seasonsText = countOf(seasons.length, WORDS.season);
    const derivedLabel = `${DERIVED} ${dateShort(asOf)}`;
    const factLabel = `${FACT} ${dateShort(asOf)}`;
    const deviations: Claim[] = [];

    for (const rep of repeats) {
      deviations.push({
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: t(M.repeat, {
          crop: cropName.get(rep.crop) ?? rep.crop,
          gap: rep.gap ? 'yes' : 'no',
          from: rep.from.label.toLowerCase(),
          to: rep.to.label.toLowerCase(),
        }),
      });
    }

    if (tally.silent > 0) {
      const shown = silentRows.slice(0, 3);
      deviations.push({
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: t(M.silent, {
          count: countOf(tally.silent, WORDS.obligation),
          hasMore: silentRows.length > 3 ? 'yes' : 'no',
          more: num(silentRows.length - 3),
          list: () => (
            <>
              {shown.map((row, i) => (
                <span key={row.key}>
                  {i > 0 ? ', ' : ''}
                  {t(M.silentItem, {
                    name: row.workName,
                    date: dateShort(row.window.to),
                    work: (chunks) => <Link to={`/cycles/${row.cycleId}/works/${row.workId}`}>{chunks}</Link>,
                  })}
                </span>
              ))}
            </>
          ),
        }),
      });
    }

    if (tally.late > 0) {
      deviations.push({
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: t(M.lateReports, { late: tally.late, reported: tally.reported }),
      });
    }

    if (qStatus?.status === 'watch') {
      deviations.push({
        evidence: 'fact',
        evidenceLabel: factLabel,
        text: t(M.watch, { date: dateShort(qStatus.since), hasCause: qStatus.cause ? 'yes' : 'no', cause: qStatus.cause ?? '' }),
      });
    }

    if (!placed) {
      deviations.push({ evidence: 'derived', evidenceLabel: derivedLabel, text: t(M.noPlacement) });
    }

    if (contour.geometry === 'candidate') {
      deviations.push({ evidence: 'candidate', evidenceLabel: t(M.candidateLabel), text: t(M.candidateGeometry) });
    }

    const causes: Claim[] = [
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text:
          tally.silent > 0
            ? t(M.causeSilent, {
                entry: (chunks) => <Link to="/ops/entry">{chunks}</Link>,
                verify: (chunks) => <Link to="/ops/verify">{chunks}</Link>,
              })
            : tally.eligible === 0
              ? t(M.causeNoDenominator)
              : t(M.causeNoSilence, { count: countOf(tally.eligible, WORDS.obligation) }),
      },
      {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text: seasons.length >= 4 ? t(M.causeDepthDeep, { seasons: seasonsText }) : t(M.causeDepthShallow, { seasons: seasonsText }),
      },
      { evidence: 'fact', evidenceLabel: t(M.causeLabLabel), text: t(M.causeLab) },
    ];

    const firstSilent = silentRows[0];
    const actions: AnswerAction[] = [
      {
        label: t(M.actOpenContour),
        to: `/contours/${contour.id}`,
        hint: t(M.actOpenContourHint),
        icon: Layers,
      },
      {
        label: t(M.actAssign),
        /*
         * Предмет доезжает до формы позицией очереди внимания: `?from=<id>`
         * подставляет заголовок, описание и привязку. Позиция берётся только
         * из тех, что форма показывает источником (верх ранга), — иначе
         * заголовок подставится, а привязка нет. Позиции по этому контуру
         * может не быть; тогда ссылка идёт пустой, и это говорится словами.
         */
        to: fromAttention ? `/ops/assignments?from=${fromAttention.id}` : '/ops/assignments',
        hint: fromAttention
          ? t(M.actAssignHintFrom, { title: fromAttention.title })
          : farm
            ? t(M.actAssignHintNoneFarm, { no: contour.no, farm: farm.name })
            : t(M.actAssignHintNone, { no: contour.no }),
        icon: ClipboardList,
      },
      firstSilent
        ? {
            label: t(M.actOpenWork, { name: firstSilent.workName }),
            to: `/cycles/${firstSilent.cycleId}/works/${firstSilent.workId}`,
            hint: t(M.actOpenWorkHint),
            icon: FileCheck,
          }
        : {
            label: t(M.actOpenFarm, { name: farmName }),
            to: `/farms/${contour.farm_id}`,
            hint: t(M.actOpenFarmHint),
            icon: Building2,
          },
    ];

    const sources: string[] = [FILES.contours, FILES.quarantine];
    if (cycleA) sources.push(FILES.obligations(cycleA.id));
    if (cycleB) sources.push(FILES.obligations(cycleB.id));

    return {
      title: t(M.answerTitle, { no: contour.no }),
      summary: {
        evidence: 'derived',
        evidenceLabel: derivedLabel,
        text:
          tally.all === 0
            ? t(M.summaryNoWorks, {
                no: contour.no,
                area: haUnit(contour.area_ha),
                category: categoryOf(contour.category),
                crop: cropOfPlaced,
                seasons: seasonsText,
                farm: farmLink,
              })
            : t(M.summaryWorks, {
                no: contour.no,
                area: haUnit(contour.area_ha),
                category: categoryOf(contour.category),
                crop: cropOfPlaced,
                eligible: countOf(tally.eligible, WORDS.obligation),
                reported: num(tally.reported),
                silent: num(tally.silent),
                seasons: seasonsText,
                farm: farmLink,
              }),
      },
      deviations,
      deviationsEmpty: t(M.deviationsNone),
      causes,
      modules: ['contours', 'cycles', 'quarantine', 'water', 'assignments'],
      sources,
      method: t(M.method2),
      actions,
    };
  }, [
    t,
    contour,
    farm,
    cropName,
    repeats,
    seasons.length,
    tally,
    silentRows,
    qStatus,
    cycleA,
    cycleB,
    asOf,
    fromAttention,
  ]);

  /* ---------------------------------------------------------------- вывод */

  if (contours.state === 'loading') {
    return (
      <Card size="lg" title={t(M.caseTitle)}>
        <p className="t-caption">{t(M.loadingRegistry)}</p>
      </Card>
    );
  }

  if (!contour) {
    return (
      <Card size="lg" title={t(M.caseTitle)}>
        <EmptyState title={t(M.notFoundTitle)} reason={t(M.notFoundReason, { id: contourId })} />
      </Card>
    );
  }

  const workColumns: Column<CaseWorkRow>[] = [
    {
      key: 'work',
      header: t(M.colWork),
      render: (row) => <Link to={`/cycles/${row.cycleId}/works/${row.workId}`}>{row.workName}</Link>,
    },
    { key: 'cycle', header: t(M.colCycle), render: (row) => row.cycleLabel },
    {
      key: 'window',
      header: t(M.colWindow),
      width: '150px',
      render: (row) => (
        <span className="num">
          {dateShort(row.window.from)} — {dateShort(row.window.to)}
        </span>
      ),
    },
    {
      key: 'state',
      header: t(M.colState),
      render: (row) => (
        <span className="case-state">
          <Badge tone={row.state === 'silent' ? 'outline' : 'sage'}>{t(STATE_MSG[row.state])}</Badge>
          {/* Медь — ритм, а не статус: «с опозданием» читается подписью. */}
          {row.late ? <Badge tone="neutral">{t(M.late)}</Badge> : null}
        </span>
      ),
    },
  ];

  return (
    <Stack>
      <Card
        size="lg"
        title={t(M.contourTitle, { no: contour.no })}
        aside={
          contour.geometry === 'none' ? (
            <Badge tone="outline">{t(M.noGeometry)}</Badge>
          ) : (
            <EvidenceMark
              kind={contour.geometry === 'candidate' ? 'candidate' : 'fact'}
              label={contour.geometry === 'candidate' ? t(M.geometryCandidate) : t(M.geometryAccepted)}
            />
          )
        }
      >
        <Stack tight>
          <Grid cols={4}>
            <KpiCard label={t(M.kpiArea)} value={ha(contour.area_ha)} unit={t(UNIT_LABELS.ha)} note={t(M.kpiAreaNote)} />
            <div className="kpi-text">
              <KpiCard label={t(M.kpiCategory)} value={categoryOf(contour.category)} note={t(M.kpiCategoryNote)} />
            </div>
            <div className="kpi-text">
              <KpiCard
                label={t(M.kpiFarm)}
                value={<Link to={`/farms/${contour.farm_id}`}>{farm?.name ?? contour.farm_id}</Link>}
                note={farm ? t(M.kpiFarmDirection, { direction: farm.direction }) : t(M.kpiFarmUnread)}
              />
            </div>
            <div className="kpi-text">
              <KpiCard
                label={t(M.kpiMassif)}
                value={<Link to={`/massifs/${contour.massif_id}`}>{massif?.name ?? contour.massif_id}</Link>}
                note={t(M.kpiMassifNote)}
              />
            </div>
          </Grid>
          <p className="t-caption">{t(M.slidesNote, { seasons: countOf(seasons.length, WORDS.season) })}</p>
        </Stack>
      </Card>

      <Card title={t(M.historyTitle)} aside={<Badge tone="neutral">{countOf(seasons.length, WORDS.season)}</Badge>}>
        {seasons.length === 0 ? (
          <EmptyState compact title={t(M.historyEmptyTitle)} reason={t(M.historyEmptyReason)} />
        ) : (
          <Stack tight>
            <div className="case-rail">
              {seasons.map((season) => {
                const crop = cropAt(contour, season.key);
                return (
                  <div className="case-rail__item" key={season.key}>
                    <div className="case-rail__season">{season.label}</div>
                    <div className="case-rail__crop">
                      <Icon icon={Sprout} size={16} />
                      {crop ? cropName.get(crop) ?? crop : t(M.notPlaced)}
                    </div>
                    <div className="case-rail__note">{crop ? t(M.railNote) : t(M.railNoNote)}</div>
                  </div>
                );
              })}
            </div>
            <p className="t-caption">
              {t(M.depthNote, { seasons: countOf(seasons.length, WORDS.season) })}{' '}
              {repeats.length > 0
                ? t(M.repeatsSome, {
                    count: num(repeats.length),
                    list: repeats
                      .map((rep) =>
                        t(M.repeatsItem, {
                          crop: cropName.get(rep.crop) ?? rep.crop,
                          from: rep.from.label.toLowerCase(),
                          to: rep.to.label.toLowerCase(),
                        }),
                      )
                      .join('; '),
                  })
                : t(M.repeatsNone)}
            </p>
          </Stack>
        )}
      </Card>

      <Card size="lg" title={t(M.worksTitle)} aside={<Badge tone="neutral">{t(M.worksBothCycles)}</Badge>}>
        {worksLoading ? (
          <p className="t-caption">{t(M.worksLoading)}</p>
        ) : !worksReadable ? (
          <EmptyState compact title={t(M.worksMissingTitle)} reason={t(M.worksMissingReason)} />
        ) : (
          <Stack tight>
            {tally.all > 0 && tally.eligible === 0 ? (
              /* Все окна впереди: DoneSplit сказал бы «работы не назначались»,
                 а они назначены — просто срок ещё не пришёл. */
              <p className="t-caption">{t(M.allAhead, { count: countOf(tally.all, WORDS.obligation) })}</p>
            ) : (
              <DoneSplit
                counts={{
                  total: tally.eligible,
                  reported: tally.reported,
                  verified: tally.verified,
                }}
                denominator={M.doneDenominator}
                windowNote={tally.upcoming > 0 ? t(M.upcomingNote, { count: num(tally.upcoming) }) : undefined}
              />
            )}
            <DataTable
              columns={workColumns}
              rows={workRows}
              getKey={(row) => row.key}
              denominator={M.worksUnit}
              empty={{ title: t(M.worksEmptyTitle), reason: t(M.worksEmptyReason) }}
            />
          </Stack>
        )}
      </Card>

      <Grid cols={2}>
        <Card title={t(M.waterTitle)}>
          {waterLoading ? (
            <p className="t-caption">{t(M.waterLoading)}</p>
          ) : water === null ? (
            <EmptyState compact icon={Droplets} title={t(M.waterEmptyTitle)} reason={t(M.waterEmptyReason)} />
          ) : (
            <Stack tight>
              <ul className="facts">
                <li className="facts__row">
                  <span className="facts__key">{t(M.waterRequested)}</span>
                  <span className="facts__val num">{m3(water.requested)}</span>
                </li>
                <li className="facts__row">
                  <span className="facts__key">{t(M.waterDelivered)}</span>
                  <span className="facts__val num">{m3(water.delivered)}</span>
                </li>
                <li className="facts__row">
                  <span className="facts__key">{t(M.waterCoverage)}</span>
                  <span className="facts__val num">{pct(share(water.delivered, water.requested), 1)}</span>
                </li>
                <li className="facts__row">
                  <span className="facts__key">{t(M.waterRequests)}</span>
                  <span className="facts__val num">
                    {t(M.waterRequestsLine, { count: num(water.count), partial: num(water.partial), pending: num(water.pending) })}
                  </span>
                </li>
              </ul>
              <p className="t-caption">{t(M.waterNote, { farm: farm?.name ?? contour.farm_id })}</p>
            </Stack>
          )}
        </Card>

        <Card title={t(M.qTitle)}>
          <Stack tight>
            {qStatus ? (
              qStatus.status === 'watch' ? (
                <p className="t-body">{t(M.qWatch, { date: dateShort(qStatus.since), hasCause: qStatus.cause ? 'yes' : 'no', cause: qStatus.cause ?? '' })}</p>
              ) : (
                <p className="t-body">{t(M.qGreen, { date: dateShort(qStatus.since) })}</p>
              )
            ) : (
              <p className="t-body">{t(M.qGreenDefault)}</p>
            )}
            {qInspections.length > 0 ? (
              <ul className="facts">
                {qInspections.map((insp) => (
                  <li className="facts__row" key={insp.id}>
                    <span className="facts__key">{dateShort(insp.date)}</span>
                    <span className="facts__val">
                      {insp.result === 'clean'
                        ? t(M.inspClean)
                        : insp.finding?.measure
                          ? t(M.inspFindingMeasure, { kind: insp.finding?.kind ?? t(M.inspKindFallback), name: insp.finding?.name ?? '—', measure: insp.finding.measure })
                          : t(M.inspFinding, { kind: insp.finding?.kind ?? t(M.inspKindFallback), name: insp.finding?.name ?? '—' })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="t-caption">{t(M.inspNone)}</p>
            )}
          </Stack>
        </Card>
      </Grid>

      <Card title={t(M.labTitle)}>
        <EmptyState compact icon={FlaskConical} title={t(M.labEmptyTitle)} reason={t(M.labEmptyReason)} />
      </Card>

      {answer ? <AnswerView answer={answer} asOf={asOf} /> : null}
    </Stack>
  );
}
